import React, { CSSProperties, useEffect, useRef } from "react";

import "./LoginButton.scss";

type GoogleLoginButtonProps = {
  style?: CSSProperties;
  boxStyle?: CSSProperties;
  clientId: string;
  disabled?: boolean;
  googleLoginText?: string;
  logoSrc?: string;
  logoStyle?: CSSProperties;
  shimmer?: boolean;
  spanStyle?: CSSProperties;
  textStyle?: CSSProperties;
  onCallback?: (data: any) => void;
  onClick?: () => void;
};

const SCRIPT_ID = "google-identity-services";
const SCRIPT_SRC = "https://accounts.google.com/gsi/client";

const GoogleLoginButton = React.forwardRef<HTMLDivElement, GoogleLoginButtonProps>((props, ref) => {
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Load Google Identity Services script
    const loadScript = () => {
      if (document.querySelector(`#${SCRIPT_ID}`)) {
        return;
      }

      const script = document.createElement("script");

      script.src = SCRIPT_SRC;
      script.id = SCRIPT_ID;
      document.head.append(script);
    };

    // Initialize Google Auth
    const initializeGoogleAuth = () => {
      if (!globalThis.google?.accounts?.id || !buttonRef.current) {
        return;
      }

      globalThis.google.accounts.id.initialize({
        auto_select: false,
        callback: props.onCallback,
        cancel_on_tap_outside: true,
        client_id: props.clientId,
      });

      globalThis.google.accounts.id.renderButton(buttonRef.current, {
        size: "large",
        width: buttonRef.current.offsetWidth,
      });
    };

    loadScript();

    if (globalThis.google?.accounts?.id) {
      initializeGoogleAuth();

      return;
    }

    const script = document.querySelector(`#${SCRIPT_ID}`);

    script?.addEventListener("load", initializeGoogleAuth);

    return () => {
      script?.removeEventListener("load", initializeGoogleAuth);
    };
  }, [props.clientId, props.onCallback]);

  const handleClick = () => {
    if (!props.disabled && !props.onClick) {
      return;
    }

    if (!props.disabled) {
      props.onClick?.();
    }
  };

  return (
    <div
      className={`google-login-container ${props.shimmer && !props.disabled ? "shimmer" : ""}`}
      style={{ height: "49.6px", position: "relative", ...props.boxStyle }}
      onClick={handleClick}
    >
      <div ref={ref} className="googleLoginButton" style={{ ...props.style }}>
        <img style={props.logoStyle} alt="" src={props.logoSrc || ""} />
        <div style={{ display: "flex", ...props.textStyle }}>
          <span style={props.spanStyle}>{props.googleLoginText ?? "ล็อกอินด้วย LINE"}</span>
        </div>
      </div>
      {!props.disabled && !props.onClick && <div ref={buttonRef} className="google-signin" />}
    </div>
  );
});

GoogleLoginButton.displayName = "GoogleLoginButton";

export default GoogleLoginButton;
